import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DuePanel } from "@/components/DuePanel";
import { IssueGrid } from "@/components/IssueGrid";
import { ProgressBar } from "@/components/ProgressBar";
import { SectionHead } from "@/components/SectionHead";
import { useActivity } from "@/hooks/useActivity";
import { useIssueViews } from "@/hooks/useIssueViews";
import { useAllMastery } from "@/hooks/useMastery";
import { useSettings } from "@/hooks/useSettings";
import { todayShort } from "@/lib/format";
import type { IssueView } from "@/types/lex";

type Filter = "all" | "active" | "done" | "upcoming";

const FILTERS: ReadonlyArray<readonly [Filter, string]> = [
  ["all", "All"],
  ["active", "Active"],
  ["done", "Completed"],
  ["upcoming", "Upcoming"],
];

export function Home() {
  const navigate = useNavigate();
  const issues = useIssueViews();
  const mastery = useAllMastery();
  const activity = useActivity();
  const { settings } = useSettings();
  const [filter, setFilter] = useState<Filter>("all");

  const filtered = useMemo<readonly IssueView[]>(() => {
    if (filter === "all") return issues;
    if (filter === "active")
      return issues.filter((i) => i.status === "in-progress" || i.status === "due-review");
    if (filter === "done") return issues.filter((i) => i.status === "done");
    if (filter === "upcoming") return issues.filter((i) => i.status === "locked");
    return issues;
  }, [filter, issues]);

  const totals = useMemo(() => {
    const totalAll = issues.reduce((acc, i) => acc + i.total, 0);
    const totalSeen = issues.reduce((acc, i) => acc + i.touched, 0);
    const totalDone = issues.filter((i) => i.status === "done").length;
    const totalDue = issues.reduce((acc, i) => acc + i.due, 0);
    return { totalAll, totalSeen, totalDone, totalDue };
  }, [issues]);

  const resumeIssue = useMemo(() => {
    return (
      issues.find((i) => i.status !== "locked" && i.touched > 0 && i.progress < i.total) ??
      issues.find((i) => i.status !== "locked" && i.touched === 0) ??
      issues[0]
    );
  }, [issues]);

  return (
    <div className="page">
      <section className="container" style={{ paddingTop: "var(--s-8)", paddingBottom: "var(--s-7)" }}>
        <div className="row-s" style={{ alignItems: "flex-start", flexWrap: "wrap", gap: "var(--s-6)" }}>
          <div className="stack" style={{ gap: "var(--s-3)", maxWidth: 720 }}>
            <div className="t-eyebrow">Day 01 → 30 · TOEIC · ETS Corpus</div>
            <h1
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: "clamp(48px, 8vw, 120px)",
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                fontWeight: 400,
              }}
            >
              A thirty-day<br />
              <span style={{ fontStyle: "italic" }}>vocabulary</span> almanac.
            </h1>
            <p
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "var(--t-lg)",
                color: "var(--text-dim)",
                maxWidth: 560,
                marginTop: "var(--s-4)",
                lineHeight: 1.5,
              }}
            >
              1,200 TOEIC words organized into 30 themed days, 40 entries each. Read · drill · review · archive.
            </p>
          </div>

          <div className="stack" style={{ gap: "var(--s-4)", minWidth: 320 }}>
            <div
              className="stack"
              style={{
                gap: "var(--s-3)",
                border: "var(--border-thin)",
                background: "var(--surface)",
                padding: "var(--s-5)",
              }}
            >
              <div className="row-s">
                <span className="t-eyebrow">Today</span>
                <span className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {todayShort()}
                </span>
              </div>
              {resumeIssue && (
                <>
                  <div className="t-display" style={{ fontSize: "var(--t-xl)", lineHeight: 1.1 }}>
                    Continue <span className="italic">Day {String(resumeIssue.day).padStart(2, "0")} — {resumeIssue.theme_en}</span>
                  </div>
                  <ProgressBar value={activity.today.learned} max={settings.dailyGoal} showLabel />
                  <div className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>
                    {activity.today.learned} new · {activity.today.reviews} answers today
                    {activity.streak > 0 && ` · ${activity.streak}-day streak`}
                  </div>
                  <button
                    className="btn btn-solid"
                    onClick={() => navigate(`/day/${resumeIssue.day}`)}
                    style={{ width: "100%", justifyContent: "center" }}
                    type="button"
                  >
                    Resume reading →
                  </button>
                </>
              )}
            </div>
            <DuePanel onStart={() => navigate("/review")} />
          </div>
        </div>
      </section>

      <hr className="rule" />

      <section className="container" style={{ padding: "var(--s-5) var(--s-6)" }}>
        <div className="row-c" style={{ gap: "var(--s-7)", flexWrap: "wrap" }}>
          <Stat label="Days completed" value={totals.totalDone} unit="/ 30" />
          <Stat label="Words mastered" value={mastery.totals.known} unit={`/ ${totals.totalAll}`} />
          <Stat label="Words seen" value={totals.totalSeen} unit={`/ ${totals.totalAll}`} />
          <Stat label="Due now" value={totals.totalDue} unit="cards" />
        </div>
      </section>

      <hr className="rule" />

      <SectionHead
        kicker="The archive · 30 days"
        title={
          <>
            <span>Browse the </span>
            <span className="italic">archive</span>.
          </>
        }
        right={
          <div className="chip-row">
            {FILTERS.map(([k, l]) => (
              <button
                key={k}
                className={`chip ${filter === k ? "is-on" : ""}`}
                onClick={() => setFilter(k)}
                type="button"
              >
                {l}
              </button>
            ))}
          </div>
        }
      />

      <section className="container">
        <IssueGrid issues={filtered} onOpen={(d) => navigate(`/day/${d}`)} />
      </section>
    </div>
  );
}

interface StatProps {
  label: string;
  value: number | string;
  unit: string;
}

function Stat({ label, value, unit }: StatProps) {
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="t-eyebrow">{label}</div>
      <div className="row-b" style={{ gap: 6 }}>
        <span
          className="t-display"
          style={{ fontSize: "var(--t-2xl)", letterSpacing: "-0.02em", lineHeight: 1 }}
        >
          {value}
        </span>
        <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)" }}>{unit}</span>
      </div>
    </div>
  );
}
